import { createSelector } from 'reselect';
import { uniqWith, eqProps, slice, isEmpty } from 'ramda';
import { isLoggedInSelector, userNumberSelector } from '../params';
import {
  SET_VIEWED_CAMPAIGNS,
  SET_RECENT_LAUNCHED_GAME,
} from '../../../constants/storage';

export const DEFAULT_USER_NUMBER = 'anonymous';
export const MAX_GAMES_COUNT = 20;

export const initialUserState = {
  recentLaunchedGames: [],
  viewedCampaigns: [],
};
export const initialState = {
  [DEFAULT_USER_NUMBER]: initialUserState,
};

const getUserState = (state, userNumber) =>
  state?.[userNumber] ?? initialUserState;

const getUserRecentLaunchedGames = (state, userNumber) =>
  getUserState(state, userNumber).recentLaunchedGames ??
  initialUserState.recentLaunchedGames;
const getUserViewedCampaigns = (state, userNumber) =>
  getUserState(state, userNumber).viewedCampaigns ??
  initialUserState.viewedCampaigns;

const getUniqList = (list) => uniqWith(eqProps('id'))(list);
const getSlicedList = (list, limit = Infinity) => slice(0, limit, list);
const getUniqSlicedList = (list, limit) =>
  getSlicedList(getUniqList(list), limit);

const reducer = (state = initialState, action) => {
  const userNumber = action?.userNumber ?? DEFAULT_USER_NUMBER;
  // Return default state for no authorized users
  if (!userNumber) return state;

  switch (action.type) {
    case SET_RECENT_LAUNCHED_GAME:
      return {
        ...state,
        [userNumber]: {
          ...getUserState(state, userNumber),
          recentLaunchedGames: getUniqSlicedList(
            [action.payload, ...getUserRecentLaunchedGames(state, userNumber)],
            MAX_GAMES_COUNT
          ),
        },
      };

    case SET_VIEWED_CAMPAIGNS:
      return {
        ...state,
        [userNumber]: {
          ...getUserState(state, userNumber),
          viewedCampaigns: action.payload,
        },
      };

    default:
      return state;
  }
};

export default reducer;

export const rootStateSelector = (state) => state?.app?.storage;

export const userNumberWithDefaultSelector = createSelector(
  userNumberSelector,
  (userNumber) => userNumber ?? DEFAULT_USER_NUMBER
);
export const recentLaunchedGamesSelector = createSelector(
  rootStateSelector,
  userNumberWithDefaultSelector,
  (rootState, userNumber) => getUserRecentLaunchedGames(rootState, userNumber)
);

export const recentGamesAvailableSelector = createSelector(
  recentLaunchedGamesSelector,
  isLoggedInSelector,
  (idList, isLoggedIn) => !isEmpty(idList) && isLoggedIn
);

export const viewedCampaignIdsSelector = createSelector(
  rootStateSelector,
  userNumberWithDefaultSelector,
  (rootState, userNumber) =>
    new Set(getUserViewedCampaigns(rootState, userNumber))
);
